import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink, Route, Routes, useLocation } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import { ConvArea } from './ConvArea';
import { ConvTemp } from './ConvTemp';
import { ConvDischarge } from './ConvDischarge';
import { ConvLength } from './ConvLength';
import { ConvMoment } from './ConvMoment';
import { ConvPower } from './ConvPower';
import { ConvPressure } from './ConvPressure';
import { ConvStress } from './ConvStress';
import { ConvVelocity } from './ConvVelocity';
import { ConvVolume } from './ConvVolume';
import { ConvWeight } from './ConvWeight';
import { ConvWtper } from './ConvWtper';

import { NewLoginForm } from './NewLoginDailogForm';
import { BackDropLoad } from './LoadingBackDrop';
import { UserContext } from './userContext';
import { PageContainer, LoginRequired } from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const UNITS = [
  { to: '/conversions-list/length',     label: 'Length' },
  { to: '/conversions-list/area',       label: 'Area' },
  { to: '/conversions-list/volume',     label: 'Volume' },
  { to: '/conversions-list/weight',     label: 'Weight' },
  { to: '/conversions-list/wtunitl',    label: 'Wt / unit L' },
  { to: '/conversions-list/stress',     label: 'Stress' },
  { to: '/conversions-list/velocity',   label: 'Velocity' },
  { to: '/conversions-list/discharge',  label: 'Discharge' },
  { to: '/conversions-list/moment',     label: 'Moment' },
  { to: '/conversions-list/power',      label: 'Power' },
  { to: '/conversions-list/pressure',   label: 'Pressure' },
  { to: '/conversions-list/temp',       label: 'Temperature' },
];

/* ─────────── Hero ─────────── */

function ConversionsHero() {
  return (
    <section
      className="
        relative isolate overflow-hidden mt-3 mb-5
        rounded-2xl border border-border-subtle
        bg-gradient-to-br from-surface-paper to-surface-elevated
      "
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10"
        style={{ background: 'radial-gradient(900px 400px at 10% -10%, rgba(251,191,36,0.16), transparent 60%), radial-gradient(700px 360px at 100% 110%, rgba(251,191,36,0.08), transparent 60%)' }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(rgba(46,46,52,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(46,46,52,0.6) 1px, transparent 1px)',
          backgroundSize: '52px 52px',
          maskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
        }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />

      <div className="px-5 sm:px-7 md:px-9 py-6 sm:py-7">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
          <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber">Field calculator</span>
        </div>
        <h1 className="text-[26px] sm:text-[32px] md:text-[36px] font-bold leading-[1.05] tracking-[-0.03em] text-fg-primary">
          Unit Conversions
        </h1>
        <div aria-hidden className="mt-3.5 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />
        <p className="mt-3.5 max-w-[640px] text-[14px] leading-[1.6] text-fg-secondary">
          Quickly convert units on the go - your essential offline tool for field calculations.
        </p>
      </div>
    </section>
  );
}

export const ConversionsList = () => {
  const location = useLocation();
  const [metadata, setMetadata] = useState(null);
  const { user } = useContext(UserContext);
  const [showLoginForm, setShowLoginForm] = useState(!user);
  const [wait, setWait] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/getsinglemetadata', { type: 'conversions' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
      else setMetadata({
        seo_title: 'Unit Conversions | ESAY',
        seo_desc: 'Quickly convert units - field calculator for engineers.',
        seo_keywords: 'unit conversions',
      });
    });
  }, []);

  return (
    <>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          <meta name="description" content={metadata.seo_desc} />
          <meta name="keywords" content={metadata.seo_keywords} />
        </Helmet>
      )}

      <BackDropLoad wait={wait} />
      <NewLoginForm
        strict
        strictTitle="Sign in to use Conversions"
        strictMessage="Unit conversion tools are available to ESAY members. Log in to access the full set of calculators."
        open={showLoginForm}
        showload={() => setWait(true)}
        hideload={() => setWait(false)}
        closeform={() => setShowLoginForm(false)}
        openloginform={() => setShowLoginForm(true)}
      />

      <PageContainer maxWidth="lg">
        <ConversionsHero />

        {/* Mobile chips */}
        <div className="md:hidden mb-4 flex flex-wrap gap-1.5">
          {UNITS.map((u) => {
            const isActive = location.pathname === u.to;
            return (
              <RouterLink
                key={u.to}
                to={u.to}
                className={`
                  inline-flex items-center rounded-full px-3 py-1.5
                  text-[12px] font-medium
                  border
                  transition-[background-color,border-color,color] duration-200 ease-out-soft
                  ${isActive
                    ? 'bg-amber text-ink border-amber'
                    : 'bg-surface-paper text-fg-secondary border-border-subtle hover:border-amber/40 hover:text-fg-primary'}
                `}
              >
                {u.label}
              </RouterLink>
            );
          })}
        </div>

        {/* Layout: sidebar (md+) + content */}
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 md:gap-5 pb-10">
          {/* Sidebar - amber bg, ink content (mirrors trends sidebar) */}
          <aside
            className="
              relative isolate overflow-hidden rounded-xl
              bg-amber-gradient
              p-2
              md:sticky md:top-[68px] md:self-start
              md:max-h-[calc(100vh-92px)]
              shadow-[0_10px_28px_-12px_rgba(10,10,11,0.45)]
              hidden md:block
            "
          >
            {/* noise overlay */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 -z-10 opacity-[0.08] mix-blend-multiply"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
              }}
            />
            {/* dark hairline at top */}
            <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-ink/35 to-transparent" />

            <nav className="flex flex-col gap-[1px]">
              {UNITS.map((u) => {
                const isActive = location.pathname === u.to;
                return (
                  <RouterLink
                    key={u.to}
                    to={u.to}
                    className="group relative flex items-center rounded-md px-2.5 py-2 transition-[background-color] duration-200 ease-out-soft"
                    style={{ backgroundColor: isActive ? '#0A0A0B' : 'transparent' }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(10,10,11,0.10)'; }}
                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <span
                      aria-hidden
                      className="pointer-events-none absolute -left-1 top-1/2 -translate-y-1/2 rounded-r-full transition-[width,opacity] duration-300 ease-out-soft"
                      style={{
                        width: isActive ? 3 : 0,
                        opacity: isActive ? 1 : 0,
                        height: '60%',
                        backgroundColor: isActive ? '#FBBF24' : '#0A0A0B',
                      }}
                    />
                    <span
                      className="text-[13px] leading-snug truncate transition-colors duration-150"
                      style={{
                        color: isActive ? '#FBBF24' : 'rgba(10,10,11,0.78)',
                        fontWeight: isActive ? 700 : 500,
                      }}
                    >
                      {u.label}
                    </span>
                  </RouterLink>
                );
              })}
            </nav>
          </aside>

          {/* Content panel */}
          <main className="min-w-0 rounded-xl border border-border-subtle bg-surface-paper p-4 sm:p-5 md:p-6">
            {user ? (
              <Routes>
                <Route path="wtunitl"   element={<><Helmet><title>Weight per unit length - Conversions | ESAY</title></Helmet><ConvWtper /></>} />
                <Route path="weight"    element={<><Helmet><title>Weight - Conversions | ESAY</title></Helmet><ConvWeight /></>} />
                <Route path="volume"    element={<><Helmet><title>Volume - Conversions | ESAY</title></Helmet><ConvVolume /></>} />
                <Route path="velocity"  element={<><Helmet><title>Velocity - Conversions | ESAY</title></Helmet><ConvVelocity /></>} />
                <Route path="stress"    element={<><Helmet><title>Stress - Conversions | ESAY</title></Helmet><ConvStress /></>} />
                <Route path="pressure"  element={<><Helmet><title>Pressure - Conversions | ESAY</title></Helmet><ConvPressure /></>} />
                <Route path="power"     element={<><Helmet><title>Power - Conversions | ESAY</title></Helmet><ConvPower /></>} />
                <Route path="moment"    element={<><Helmet><title>Moment - Conversions | ESAY</title></Helmet><ConvMoment /></>} />
                <Route path="length"    element={<><Helmet><title>Length - Conversions | ESAY</title></Helmet><ConvLength /></>} />
                <Route path="area"      element={<><Helmet><title>Area - Conversions | ESAY</title></Helmet><ConvArea /></>} />
                <Route path="temp"      element={<><Helmet><title>Temperature - Conversions | ESAY</title></Helmet><ConvTemp /></>} />
                <Route path="discharge" element={<><Helmet><title>Discharge - Conversions | ESAY</title></Helmet><ConvDischarge /></>} />
              </Routes>
            ) : (
              <LoginRequired
                title="Sign in to use Conversions"
                message="Unit conversion calculators are available to ESAY members. Sign in to access the full set of tools."
                onLogin={() => setShowLoginForm(true)}
              />
            )}
          </main>
        </div>
      </PageContainer>
    </>
  );
};
